"use strict";

import _ from "lodash";
import { Available } from "../../../gateway/Available/Available";
import { CalculatingMachineV2Gateway } from "../../../gateway/CalculatingMachineV2Gateway/CalculatingMachineV2Gateway";
import * as DateUtils from "../../../utils/date/date";
import { deepClonePoints } from "../CloningService/point";
import { CalculatingMachineMiddleware } from "./CalculateMachineMiddleware";
import { CalcOperation } from "./ProcessorCalculatingMachine/Operators/CalcOperations";

export class CalculatingMachine extends CalculatingMachineMiddleware {
    constructor({ Serie, Point, PointCalculated } = {}) {
        super(Serie);

        this.Point = Point;

        this.PointCalculated = PointCalculated;

        this.showDisabled = false;

        this.opt = {};

        this.pointsArray = [];

        this.pointsRelation = [];

        this.pointsCalcArray = [];

        this.times = null;

        this.groupFilterOper = null;

        this.totalGroupCompanies = null;

        this.calculatingMachineV2Gateway = new CalculatingMachineV2Gateway();

        this.sourcesDepartaments = [
            "tax",
            "personnel",
            "fin",
            "simplesNacional",
            "eSocial",
            "accounting"
        ];
    }

    resultComparation({
        companyId,
        serieId,
        scenario = "ORIGINAL",
        period = "monthly",
        timesBetween = {},
        options = {}
    }) {
        const visualizationAccumulated = options.hasOwnProperty(
            "visualizationAccumulated"
        )
            ? options.visualizationAccumulated
            : undefined;

        if (_.isEmpty(this.ProcessorCalculatingMachine)) return null;
        if (this.sourcesDepartaments.includes(this.source)) {
            return [
                // const preCalculatedPoints = [
                ...this.calculatingMachineV2Gateway.getCalculatedSeriesPoints(
                    [serieId],
                    timesBetween.startDate,
                    scenario,
                    period,
                    visualizationAccumulated
                ),
                ...this.calculatingMachineV2Gateway.getCalculatedSeriesPoints(
                    [serieId],
                    timesBetween.endDate,
                    scenario,
                    period,
                    visualizationAccumulated
                )
            ];
        }
        // if (preCalculatedPoints.length) {
        //     return preCalculatedPoints;
        // }

        const pointsComparationCalculated = this.PointCalculated.findCollection(
            {
                s: serieId,
                $or: [
                    { t: { $eq: timesBetween.startDate } },
                    { t: { $eq: timesBetween.endDate } }
                ],
                period,
                sc: scenario
            },
            {},
            "array"
        );

        if (!_.isEmpty(pointsComparationCalculated))
            return pointsComparationCalculated;

        const result = this.resultPointsCalculator(
            companyId,
            scenario,
            period,
            options,
            timesBetween,
            serieId
        );

        const pointsComparation = _.filter(result, (point) => {
            if (point.t === 0) return point;

            if (
                point.s === serieId &&
                (point.t.getTime() === timesBetween.startDate.getTime() ||
                    point.t.getTime() === timesBetween.endDate.getTime())
            ) {
                return point;
            }
        });

        return pointsComparation;
    }

    /**
     * @public
     * Método recebe series e retona os points
     * @param {Object} param
     */
    result({
        companyId,
        serieIds,
        scenario = "ORIGINAL",
        period = "monthly",
        timesBetween,
        fakeData,
        isGroupCompanies,
        options = {}
    }) {
        const visualizationAccumulated = options.hasOwnProperty(
            "visualizationAccumulated"
        )
            ? options.visualizationAccumulated
            : undefined;

        if (fakeData)
            return this.generateFakePoints({ serieIds, timesBetween });

        if (
            this.Point.findCollection(
                { s: { $in: serieIds }, driller: true },
                {},
                "array"
            ).length
        ) {
            const points = this.resultPointsCalculator(
                companyId,
                scenario,
                period,
                options,
                timesBetween,
                serieIds
            );

            this.calculatingMachineV2Gateway.setDrillerPoints(points);
        }

        if (this.sourcesDepartaments.includes(this.source)) {
            const pointsResult =
                this.calculatingMachineV2Gateway.getCalculatedSeriesPointsInRange(
                    _.uniq(serieIds),
                    scenario,
                    period,
                    timesBetween,
                    visualizationAccumulated
                );
            if (options.filterProjectionPoints)
                return pointsResult.filter((p) => !p.p);
            return pointsResult;
        }
    }

    resultPointsCalculator(
        companyId,
        scenario,
        period,
        options,
        timesBetween,
        serieId
    ) {
        this._setCalcParameters({
            companyId,
            scenario,
            period,
            options,
            timesBetween: {
                startDate: DateUtils.addToDate(
                    timesBetween.startDate,
                    -12,
                    "months"
                ),
                endDate: timesBetween.endDate
            },
            isGroupCompanies: options.isGroupCompanies
        });

        const serieIdsArr = [].concat(serieId);

        const serieIds = _.filter(serieIdsArr, (v) => v != null);

        let result = this._resultRec(serieIds);

        let sids = _.uniq(this.pointsCalcArray.map((p) => p.s));

        if (sids.length) result = result.filter((p) => !sids.includes(p.s));

        result.concat(this.pointsCalcArray);

        this._commit();

        if (options.hasOwnProperty("visualizationAccumulated")) {
            result = this.formatPointsBySerieVisualization(
                result,
                options.visualizationAccumulated
            );
        }

        if (this.source.includes(this.source))
            result = result.map((p) => ({ ...p, period }));
        return result;
    }

    formatPointsBySerieVisualization(points, isAccumulated = false) {
        let pointsBySeries = [];

        const pointsImmutable = deepClonePoints(points);

        const serieIdsInPoints = _.uniq(pointsImmutable.map((p) => p.s));

        const isOriginal = this.scenario === "ORIGINAL";

        const _localSerieFormat = (
            pointsBySeries,
            currentSerie,
            localPointsBySerie,
            isAccumulated
        ) => {
            const pointsAccumulated =
                this.ProcessorCalculatingMachine.accumulator(
                    currentSerie,
                    localPointsBySerie,
                    this.period,
                    this.scenario,
                    _.get(currentSerie, "metadata.periodAccumulated", true) ===
                        false,
                    isAccumulated
                );

            return _.concat(pointsAccumulated, pointsBySeries);
        };

        const _accountingAccumulator = (arr, point, serie, classifier) => {
            if (
                !isOriginal &&
                isAccumulated &&
                !_.get(serie.metadata, "accumulated") &&
                classifier >= 3
            ) {
                let previousVal = 0;
                if (parseInt(DateUtils.dateToString(point.t, "M")) !== 1) {
                    previousVal = _.get(
                        _.find(arr, {
                            t: new Date(CalcOperation.helperMonths(point.t, -1))
                        }),
                        "m.v",
                        0
                    );
                }

                point.m.v = _.get(point, "m.v", 0) + previousVal;
            } else if (!point.m.v && isAccumulated && isOriginal) {
                point.m.v = _.get(point, "m.f", 0);
            } else if (!point.m.v && isOriginal) {
                point.m.v = super.valueGet(serie, point, {}, { isAccumulated });
            }

            return point;
        };

        _.forEach(serieIdsInPoints, (serieId) => {
            const currentSerie = this.Serie.findOneCollection({ _id: serieId });
            let source = _.isArray(currentSerie.sources)
                ? _.get(currentSerie, "sources[0]", "accounting")
                : currentSerie.sources;

            currentSerie.classifier =
                (currentSerie.classifier &&
                    currentSerie.classifier.toString()) ||
                currentSerie.classifier;

            const classifier = currentSerie.classifier
                ? parseInt(currentSerie.classifier.charAt(0))
                : "";

            const localPointsBySerie = deepClonePoints(
                _.sortBy(
                    pointsImmutable.filter((p) => p.s === serieId),
                    "t"
                )
            );

            if (source === "accounting") {
                pointsBySeries = _.concat(
                    localPointsBySerie.map((localPoint, _idx, arr) =>
                        _accountingAccumulator(
                            arr,
                            localPoint,
                            currentSerie,
                            classifier
                        )
                    ),
                    pointsBySeries
                );

                if (this.period !== "monthly" && !currentSerie.exp) {
                    pointsBySeries = pointsBySeries.map((p, _i, arr) => {
                        const subArr = _.filter(
                            arr,
                            (sub) => new Date(sub.t).getFullYear() === new Date(p.t).getFullYear()
                        );
                        const idx = _.findIndex(
                            subArr,
                            (s) => new Date(s.t).getTime() === new Date(p.t).getTime()
                        );
                        if (idx > 0 && classifier < 3)
                            p.m.v += subArr[idx - 1]["m"]["v"];

                        return p;
                    });
                }
            } else if (source === "financial" || source === "fin") {
                pointsBySeries = _localSerieFormat(
                    pointsBySeries,
                    currentSerie,
                    localPointsBySerie,
                    isAccumulated
                );
            } else if (
                this.sourcesDepartaments.includes(source) &&
                !_.get(currentSerie, "exp", false)
            ) {
                pointsBySeries = _localSerieFormat(
                    pointsBySeries,
                    currentSerie,
                    localPointsBySerie,
                    isAccumulated
                );
            } else {
                pointsBySeries = _.concat(pointsBySeries, localPointsBySerie);
            }
        });

        return pointsBySeries;
    }

    _setCalcParameters({
        companyId,
        scenario,
        period,
        options,
        timesBetween,
        isGroupCompanies
    } = {}) {
        let _timesBetween = {};

        if (!_.isEmpty(timesBetween)) {
            const { startDate, endDate } = timesBetween;

            const beginOfStartDate = new Date(
                `${new Date(startDate).getFullYear()}/01/01`
            );

            _timesBetween = {
                $and: [
                    { t: { $gte: beginOfStartDate } },
                    { t: { $lte: new Date(endDate) } }
                ]
            };
        }

        if (options.ignoreAvailable) this.opt.ignoreAvailable = true;
        if (options.monthlyFinancialCalc)
            this.opt.monthlyFinancialCalc = options.monthlyFinancialCalc;
        if (options.ignoreAccountResult)
            this.opt.ignoreAccountResult = options.ignoreAccountResult;
        if (options.showDisabled) this.showDisabled = options.showDisabled;
        if (options.recalculate) {
            this.pointsCalcArray = [];
        } else {
            const pointsCalcArray = this.PointCalculated.findCollection(
                {
                    period,
                    sc: scenario
                },
                {},
                "array"
            );
            this.pointsCalcArray = this.filterPointByTimesBetween(
                pointsCalcArray,
                _timesBetween
            );
        }

        this.opt.accumulatedView = options.visualizationAccumulated;

        const isCashflow = this.Serie.findOneCollection({
            "metadata.cashFlow": true
        });

        if (isCashflow) {
            this.opt.isCashflow = true;
        } else {
            this.opt.isCashflow = false;
        }

        this.companyId = companyId;
        this.period = period;
        this.scenario = scenario;

        this.available = new Available().get({
            companyId: companyId,
            scenarioId: scenario,
            source: this.source
        });

        if (this.available) this.available = _.uniq(this.available);

        if (scenario) {
            const pointsArray = this.Point.findCollection(
                {
                    c: companyId,
                    sc: scenario
                },
                {},
                "array"
            );
            this.pointsArray = this.filterPointByTimesBetween(
                pointsArray,
                _timesBetween
            );
        }

        if (isGroupCompanies) {
            this.groupFilterOper = options.groupFilterOper;
            this.totalGroupCompanies = options.totalGroupCompanies;
        }

        if (this.source === "simplesNacional") {
            this.opt.isSimplesNacional = true;
        }
    }

    filterPointByTimesBetween(points, timesBetween) {
        if (_.isEmpty(timesBetween)) return points;
        const gte = timesBetween?.["$and"]?.[0]?.["t"]?.["$gte"];
        const lte = timesBetween?.["$and"]?.[1]?.["t"]?.["$lte"];
        if (!gte || !lte) return points;
        return points.filter((p) => {
            return +new Date(p.t) >= +gte && +new Date(p.t) <= +lte;
        });
    }

    _resultRec(serieIds, result = []) {
        if (!serieIds.length) return result;

        let serieId = _.head(serieIds.splice(0, 1));

        const serie = this.Serie.findOneCollection({ _id: serieId });

        if (!serie) {
            return this._resultRec(serieIds, result);
        }

        let pointsCalculated = _.filter(
            this.pointsCalcArray,
            (p) => p.s === serieId
        );

        if (pointsCalculated.length) {
            result = result.concat(pointsCalculated);
            return this._resultRec(serieIds, result);
        }

        const getPoint = this._pointGet(serie);
        const points = getPoint || [];

        result = result.concat(...points);

        return this._resultRec(serieIds, result);
    }

    /**
     *
     * @param {Object} serie
     */
    _pointGet(serie) {
        const source = _.isArray(serie.sources)
            ? _.head(serie.sources)
            : serie.sources;

        if (serie.type == "const") {
            const pointsDependecy = this._resultRec(serie.compound);

            let pointsCalculated = [];

            if (!this.available.length) return [];

            const availableByYears = _.groupBy(this.available, (v) => {
                let time = DateUtils.getDate(v);

                return time.getFullYear();
            });

            const lastValues = {};

            const pointsCashStarterYears =
                this.Point.findCollection(
                    { c: this.companyId, s: "CASH_BALANCE", sc: "ORIGINAL" },
                    {},
                    "array"
                ) || [];

            _.forEach(availableByYears, (datesByYear, year) => {
                datesByYear.forEach((availableDate, index) => {
                    const time = DateUtils.getDate(availableDate);

                    const prevusTime = DateUtils.getPreviousMonth(time);

                    const pointDependecy = pointsDependecy.find(
                        (p) => +p.t == +prevusTime
                    );

                    let value = 0;

                    if (index == 0) {
                        const pointStart = pointsCashStarterYears.find(
                            (p) => year == p.t
                        );

                        value = pointStart ? pointStart.m.v : 0;
                    } else {
                        const lastValueByYear = _.get(lastValues, `${year}`);

                        value =
                            lastValueByYear[index - 1] +
                            _.get(pointDependecy, "m.v", 0);
                    }

                    if (!_.get(lastValues, `${year}`))
                        lastValues[year] = [value];
                    else lastValues[year].push(value);

                    pointsCalculated.push({
                        t: time,
                        s: serie._id,
                        c: serie.companyId,
                        sc: this.scenario,
                        u: DateUtils.getDate(new Date()),
                        m: { v: value },
                        period: this.period
                    });
                });
            });

            return pointsCalculated;
        }

        if (
            _.get(serie, "parametrizeIds.length") &&
            this.scenario === "ORIGINAL"
        ) {
            const pointsParametrize = this._parametrizeGet(serie);

            return pointsParametrize;
        } else if (serie.type == "raw") {
            const _points = _.filter(
                deepClonePoints(this.pointsArray),
                (p) => p.s === serie._id
            );

            const isDisabled = _.get(serie, "metadata.disabled", false);

            const pointsSerieModified = _points.map((p) => {
                if (isDisabled && !this.showDisabled) {
                    p.m = { v: 0, f: 0, m: 0, o: 0 };
                }

                p.period = this.period;

                if (this.sourcesDepartaments.includes(this.source))
                    p.period = "monthly";

                if (source === "accounting") {
                    p.reducing = _.get(serie.metadata, "reducing");
                    p.positive = _.get(serie.metadata, "positive");
                    p.negative = _.get(serie.metadata, "negative");
                } else if (this.sourcesDepartaments.includes(source)) {
                    if (!p.m.v) p.m.v = p.m.f || p.m.r || 0;
                } else {
                    p.cashflow = this.opt.isCashflow;
                }

                return p;
            });

            return pointsSerieModified;
        }

        if (
            serie.type == "compound" &&
            ["synthetic", "analytic"].includes(serie.genus)
        ) {
            const pointsAssociated = this._associatedGet(serie);

            return pointsAssociated;
        }

        if (serie.type == "constant") {
            return this._constantPointFind(serie._id);
        }

        if (!_.isEmpty(serie.exp)) {
            let pointsCompound = this._compoundGet(serie);

            if (serie.groupId) {
                pointsCompound = _.uniqWith(
                    pointsCompound,
                    (a, b) =>
                        a.c === b.c &&
                        a.t.getTime() === b.t.getTime() &&
                        a.s === b.s &&
                        a.sc === b.sc
                );
            }

            const pointsCalculated = this.ProcessorCalculatingMachine.run(
                serie,
                pointsCompound,
                this.scenario,
                this.period,
                this.available,
                { ...this.opt, avg: this.groupFilterOper === "average" },
                source,
                this.totalGroupCompanies || 1
            );

            this.pointsCalcArray =
                this.pointsCalcArray.concat(pointsCalculated);

            return pointsCalculated;
        } else if (serie.referenceId && serie.sources[0] === "accounting") {
            let points = [];
            const referenceIds = [].concat(serie.referenceId);
            const pointsRelation = this._resultRec(referenceIds);

            if (pointsRelation.length > 0) {
                points = this._summarizeBySerie(serie, pointsRelation);
            }

            return points;
        }
    }

    _parametrizeGet(serie) {
        const source = _.isArray(serie.sources)
            ? serie.sources[0]
            : serie.sources;

        const serieIds = serie.parametrizeIds;

        let pointsAssociated = [];

        for (let i in serieIds) {
            let points = this._filterPoints(
                {
                    _id: serieIds[i],
                    companyId: this.companyId,
                    sources: serie.sources
                },
                source
            );

            pointsAssociated = _.concat(pointsAssociated, points);
        }

        if (serieIds.length > 1) {
            pointsAssociated = this._summarizeBySerie(serie, pointsAssociated);

            pointsAssociated = pointsAssociated.map((point) => {
                point.c = serie.companyId;
                point.s = serie._id;

                point.period = this.period;
                point.period = "monthly";

                if (!point.m.v) {
                    point.m.v = _.get(point, "m.m", _.get(point, "m.f", 0));
                }
                return point;
            });
        } else if (serieIds.length) {
            pointsAssociated = pointsAssociated.map((point) => {
                point.c = serie.companyId;
                point.s = serie._id;

                point.period = this.period;
                point.period = "monthly";

                if (!point.m.v) {
                    point.m.v = _.get(point, "m.m", _.get(point, "m.f", 0));
                }
                return point;
            });
        }

        return pointsAssociated;
    }

    /**
     * @private
     * Péga os dados dependetes de uma formula para calcular
     */
    _compoundGet(serie) {
        return this._resultRec(serie.compound);
    }

    /**
     * @private
     * Péga os dados dependetes de uma formula para calcular
     */
    _associatedGet(serie) {
        const source = _.isArray(serie.sources)
            ? serie.sources[0]
            : serie.sources;
        let pointsAssociated = [];

        if (this.scenario === "ORIGINAL") {
            let serieIds = [];

            if (serie.associatedRaw) {
                serieIds = serie.associatedRaw;

                for (let i in serieIds) {
                    let points = this._filterPoints(
                        {
                            _id: serieIds[i],
                            companyId: this.companyId,
                            sources: serie.sources
                        },
                        source
                    );

                    pointsAssociated = _.concat(pointsAssociated, points);
                }
            } else {
                let seriesAssociated = this.Serie.findCollection(
                    { companyId: this.companyId, associated: serie._id },
                    { fields: { _id: 1 } },
                    "array"
                );

                serieIds = seriesAssociated.map((serie) => serie._id);

                if (serieIds.length) {
                    pointsAssociated = this._resultRec(serieIds);
                }

                if (!seriesAssociated.length && serie.sources == "custom") {
                    // serieIds = [serie._id];
                    pointsAssociated = this._filterPoints(serie, source);
                    return pointsAssociated;
                }
            }

            if (serieIds.length > 1) {
                pointsAssociated = this._summarizeBySerie(
                    serie,
                    pointsAssociated
                );
            } else if (serieIds.length) {
                pointsAssociated = pointsAssociated.map((point) => {
                    point.c = serie.companyId;
                    point.s = serie._id;

                    point.period = this.period;

                    if (this.sourcesDepartaments.includes(this.source))
                        point.period = "monthly";

                    if (source === "accounting") {
                        point.reducing = _.get(serie.metadata, "reducing");
                        point.positive = _.get(serie.metadata, "positive");
                        point.negative = _.get(serie.metadata, "negative");
                    } else if (source === "financial") {
                        point.cashflow = this.opt.isCashflow;
                    }

                    return point;
                });
            }
        } else {
            pointsAssociated = this._filterPoints(serie, source);
        }

        if (
            source === "accounting" &&
            this.groupFilterOper &&
            this.groupFilterOper === "average"
        ) {
            const total = this.totalGroupCompanies;
            pointsAssociated.forEach((r) => {
                Object.keys(r.m).forEach((key) => {
                    if (r["m"][key] !== 0) {
                        r["m"][key] = +(r["m"][key] / total).toFixed(2);
                    }
                });
            });
        }

        return pointsAssociated;
    }

    _filterPoints(serie, source) {
        const pointsAssociated = deepClonePoints(
            this.pointsArray.filter((p) => p.s === serie._id)
        );

        for (let point of pointsAssociated) {
            point.c = serie.companyId;
            point.s = serie._id;
            point.period = this.period;

            if (this.sourcesDepartaments.includes(this.source))
                pointsAssociated[i].period = "monthly";

            if (source === "accounting") {
                point.reducing = serie.metadata && serie.metadata.reducing;
                point.positive = serie.metadata && serie.metadata.positive;
                point.negative = serie.metadata && serie.metadata.negative;
            } else if (source === "financial") {
                point.cashflow = this.opt.isCashflow;
            }
        }
        return pointsAssociated;
    }

    /**
     * @private
     * @param {Object} serie
     * @param {Array} pointsAssociated
     */
    _summarizeBySerie(serie, pointsAssociated) {
        const _sumPointsAssociates = (pointMIncome, pointMAssociated) => {
            let keyIncome = Object.keys(pointMIncome);
            let keyAssociated = Object.keys(pointMAssociated);
            let keysIntersection = _.filter(keyAssociated, (value) =>
                _.includes(keyIncome, value)
            );

            const sumProperties = {};

            keysIntersection.forEach((key) => {
                sumProperties[key] = pointMIncome[key] + pointMAssociated[key];
            });

            return {
                ...pointMIncome,
                ...sumProperties
            };
        };

        const summarizedPointAssociated = pointsAssociated.reduce(
            (pointsAssociatedArray, point) => {
                let pointTimeIndex = _.findIndex(
                    pointsAssociatedArray,
                    (pArray) => pArray.t.getTime() === point.t.getTime()
                );

                if (pointTimeIndex >= 0) {
                    pointsAssociatedArray[pointTimeIndex].m =
                        _sumPointsAssociates(
                            point.m,
                            pointsAssociatedArray[pointTimeIndex].m
                        );
                } else {
                    delete point._id;

                    pointsAssociatedArray.push({
                        ...point,
                        s: serie._id,
                        c: serie.companyId,
                        classifier: serie.classifier
                    });
                }

                return pointsAssociatedArray;
            },
            []
        );

        return summarizedPointAssociated;
    }

    /**
     * @function _constantPointFind
     * @description Busca Points de Series constantes se caso existir
     * para a Empresa se não deve retornar o Point default Nucont da Serie solicitada
     * @param {string} serieId
     * @returns {Array} Array of Points or an empty list
     */
    _constantPointFind(serieId) {
        let points = [];

        let serieConstant = this.Serie.findOneCollection({ _id: serieId });

        if (!_.isEmpty(serieConstant)) {
            const s_id = _.get(
                serieConstant,
                "associatedRaw.0",
                serieConstant._id
            );

            points = this.Point.findCollection({ s: s_id }, {}, "array");

            points.map((p) => {
                p.s = serieId;
                p.t = 0;
                p.isConstant = true;
                return p;
            });
        }

        return points;
    }

    _commit() {
        this.PointCalculated.upsert(this.pointsCalcArray);

        this.pointsCalcArray = [];

        this.pointsArray = [];
    }
}
